import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PAGE_SIZE } from '../constants/departmentMeta'
import { departmentApi, type DepartmentListQuery } from '../api/departmentApi'
import type { Department, DeptStatus } from '../types/department'

export type SortField = 'name' | 'code' | 'employees' | 'createdDate'
export type SortOrder = 'asc' | 'desc'

export function useDepartment() {
  const [departments, setDepartments] = useState<Department[]>([])
  const [totalItems, setTotalItems] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingDept, setEditingDept] = useState<Department | null>(null)
  const [viewModalDept, setViewModalDept] = useState<Department | null>(null)
  const [deleteModalDept, setDeleteModalDept] = useState<Department | null>(null)

  const fetchDepartments = useCallback(async () => {
    setIsLoading(true)

    const queryParams: DepartmentListQuery = {
      page: currentPage,
      limit: PAGE_SIZE,
      search: search || undefined,
      isActive: statusFilter === 'Active' ? true : statusFilter === 'Inactive' ? false : undefined,
    }

    try {
      const res = await departmentApi.listDepartments(queryParams)
      const items = Array.isArray(res?.items) ? res.items : []
      const colors = ['#3b82f6', '#7c3aed', '#16a34a', '#dc2626', '#ea580c', '#0891b2', '#059669']
      const mapped: Department[] = items.map((dto: any, idx: number) => ({
        id: dto.id || `dept-${idx}`,
        name: dto.departmentName || dto.name || 'Unnamed Department',
        description: dto.description || '',
        code: dto.departmentCode || dto.code || 'DEPT',
        employees: dto.employeeCount ?? dto.employee_count ?? dto.employees ?? 0,
        status: dto.isActive !== false && dto.status !== 'Inactive' ? 'Active' : 'Inactive',
        createdDate: dto.createdAt
          ? new Date(dto.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : (dto.createdDate || ''),
        accentColor: dto.accentColor || colors[idx % colors.length],
      }))

      setDepartments(mapped)
      setTotalItems(typeof res.total === 'number' ? res.total : mapped.length)
    } catch {
      setDepartments([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, search, statusFilter])

  useEffect(() => {
    fetchDepartments()
  }, [fetchDepartments])

  const handleToggleStatus = async (id: string | number) => {
    const dept = departments.find((d) => d.id === id)
    if (!dept) return
    const nextStatus: DeptStatus = dept.status === 'Active' ? 'Inactive' : 'Active'
    
    setDepartments((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: nextStatus } : d))
    )

    try {
      await departmentApi.updateDepartment(String(id), { isActive: nextStatus === 'Active' })
    } catch {
      // Optimistic local update
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const filtered = departments.filter((d) => {
    const matchesSearch =
      search === '' ||
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.code.toLowerCase().includes(search.toLowerCase())
    const matchesStatus = statusFilter === '' || d.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const sorted = [...filtered].sort((a, b) => {
    let comparison = 0
    if (sortField === 'name') {
      comparison = a.name.localeCompare(b.name)
    } else if (sortField === 'code') {
      comparison = a.code.localeCompare(b.code)
    } else if (sortField === 'employees') {
      comparison = a.employees - b.employees
    } else if (sortField === 'createdDate') {
      comparison = a.createdDate.localeCompare(b.createdDate)
    }
    return sortOrder === 'asc' ? comparison : -comparison
  })

  const totalDepts = totalItems || departments.length
  const activeDepts = departments.filter((d) => d.status === 'Active').length
  const inactiveDepts = departments.filter((d) => d.status === 'Inactive').length
  const totalEmployees = departments.reduce((sum, d) => sum + d.employees, 0)

  const totalPages = Math.max(1, Math.ceil(totalDepts / PAGE_SIZE))

  return {
    isLoading,
    departments,
    setDepartments,
    totalItems: totalDepts,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    sortField,
    sortOrder,
    handleSort,
    setSortOrder,
    currentPage,
    setCurrentPage,
    isAddModalOpen,
    setIsAddModalOpen,
    editingDept,
    setEditingDept,
    viewModalDept,
    setViewModalDept,
    deleteModalDept,
    setDeleteModalDept,
    handleToggleStatus,
    filtered,
    sorted,
    totalDepts,
    activeDepts,
    inactiveDepts,
    totalEmployees,
    totalPages,
    paginated: departments.length > PAGE_SIZE
      ? sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
      : sorted,
    refetchDepartments: fetchDepartments,
  }
}

/**
 * Shared React Query hook for all departments across the application.
 * Caches departments with a 5-minute staleTime and prevents client 4xx retries.
 */
export function useDepartmentsQuery(branchId?: string, isActive = true, enabled = true) {
  return useQuery({
    queryKey: ['departments', 'all', branchId || '', isActive],
    queryFn: () => departmentApi.getAllDepartments(branchId, isActive),
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}
